import * as ewe from "@eyeo/webext-ad-filtering-solution";
import { DialogAction, } from "../shared/types.js";
import { ValidationReason, } from "../shared/validation-reasons.js";
import { isUrlAllowed } from "./url-allowlist.js";
const serverLogger = new ewe.telemetry.ServerLogger("reverse_trial");
function invalid(reason) {
    serverLogger.info("config_validation_failed", { reason });
    return null;
}
export function isObject(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
function isPositiveNumber(value) {
    return typeof value === "number" && Number.isFinite(value) && value > 0;
}
function isNonNegativeNumber(value) {
    return typeof value === "number" && Number.isFinite(value) && value >= 0;
}
function parseAction(value, allowedUrlHosts) {
    if (!isObject(value)) {
        return invalid(ValidationReason.ACTION_SHAPE_INVALID);
    }
    switch (value.type) {
        case DialogAction.CLOSE:
            return { type: DialogAction.CLOSE };
        case DialogAction.END_TRIAL:
            return { type: DialogAction.END_TRIAL };
        case DialogAction.START_TRIAL:
            return { type: DialogAction.START_TRIAL };
        case DialogAction.NAVIGATE: {
            if (typeof value.url !== "string" || value.url.length === 0) {
                return invalid(ValidationReason.NAVIGATE_URL_MISSING);
            }
            if (!isUrlAllowed(value.url, allowedUrlHosts)) {
                return invalid(ValidationReason.NAVIGATE_URL_NOT_ALLOWLISTED);
            }
            const action = {
                type: DialogAction.NAVIGATE,
                url: value.url,
            };
            if (typeof value.closeAfter === "boolean") {
                action.closeAfter = value.closeAfter;
            }
            return action;
        }
        default:
            return invalid(ValidationReason.ACTION_TYPE_UNKNOWN);
    }
}
function parseStepElement(value, allowedUrlHosts) {
    if (!isObject(value)) {
        return invalid(ValidationReason.STEP_ELEMENT_SHAPE_INVALID);
    }
    if (typeof value.key !== "string" || value.key.length === 0) {
        return invalid(ValidationReason.STEP_ELEMENT_KEY_MISSING);
    }
    const action = parseAction(value.action, allowedUrlHosts);
    if (!action) {
        return null;
    }
    return { key: value.key, action };
}
function parseBody(value) {
    if (!Array.isArray(value) || value.length === 0) {
        return invalid(ValidationReason.STEP_BODY_INVALID);
    }
    const body = [];
    for (const entry of value) {
        if (!isObject(entry) || typeof entry.key !== "string") {
            return invalid(ValidationReason.STEP_BODY_ENTRY_INVALID);
        }
        body.push({ key: entry.key });
    }
    return body;
}
function parseI18n(value) {
    if (!isObject(value) || !isObject(value.en)) {
        return invalid(ValidationReason.STEP_I18N_EN_MISSING);
    }
    const i18n = {};
    for (const [locale, entries] of Object.entries(value)) {
        if (!isObject(entries)) {
            return invalid(ValidationReason.STEP_I18N_ENTRIES_INVALID);
        }
        const localeEntries = {};
        for (const [k, v] of Object.entries(entries)) {
            if (typeof v !== "string") {
                return invalid(ValidationReason.STEP_I18N_VALUE_NOT_STRING);
            }
            localeEntries[k] = v;
        }
        i18n[locale] = localeEntries;
    }
    return i18n;
}
function allReferencedKeysExistInEn(title, body, ctaButton, links, i18n) {
    const required = new Set();
    required.add(title.key);
    for (const b of body) {
        required.add(b.key);
    }
    if (ctaButton) {
        required.add(ctaButton.key);
    }
    if (links) {
        for (const l of links) {
            required.add(l.key);
        }
    }
    for (const key of required) {
        if (typeof i18n.en[key] !== "string") {
            return false;
        }
    }
    return true;
}
function parseStep(value, allowedUrlHosts) {
    if (!isObject(value)) {
        return invalid(ValidationReason.STEP_SHAPE_INVALID);
    }
    if (typeof value.id !== "number" || !Number.isFinite(value.id)) {
        return invalid(ValidationReason.STEP_ID_INVALID);
    }
    if (!isNonNegativeNumber(value.minElapsedDays)) {
        return invalid(ValidationReason.STEP_MIN_ELAPSED_DAYS_INVALID);
    }
    let maxElapsedDays;
    if (value.maxElapsedDays !== undefined) {
        if (!isNonNegativeNumber(value.maxElapsedDays)) {
            return invalid(ValidationReason.STEP_MAX_ELAPSED_DAYS_INVALID);
        }
        if (value.maxElapsedDays < value.minElapsedDays) {
            return invalid(ValidationReason.STEP_MAX_ELAPSED_DAYS_INVALID);
        }
        maxElapsedDays = value.maxElapsedDays;
    }
    if (!isObject(value.title) || typeof value.title.key !== "string") {
        return invalid(ValidationReason.STEP_TITLE_INVALID);
    }
    const title = { key: value.title.key };
    const body = parseBody(value.body);
    if (!body) {
        return null;
    }
    let ctaButton;
    if (value.ctaButton !== undefined) {
        const parsed = parseStepElement(value.ctaButton, allowedUrlHosts);
        if (!parsed) {
            return null;
        }
        ctaButton = parsed;
    }
    let links;
    if (value.links !== undefined) {
        if (!Array.isArray(value.links)) {
            return invalid(ValidationReason.STEP_LINKS_INVALID);
        }
        const parsedLinks = [];
        for (const link of value.links) {
            const parsed = parseStepElement(link, allowedUrlHosts);
            if (!parsed) {
                return null;
            }
            parsedLinks.push(parsed);
        }
        links = parsedLinks;
    }
    let showCloseButton;
    if (value.showCloseButton !== undefined) {
        if (typeof value.showCloseButton !== "boolean") {
            return invalid(ValidationReason.STEP_SHOW_CLOSE_BUTTON_INVALID);
        }
        showCloseButton = value.showCloseButton;
    }
    let autoDismissMs;
    if (value.autoDismissMs !== undefined) {
        if (value.autoDismissMs !== null &&
            (typeof value.autoDismissMs !== "number" ||
                !Number.isFinite(value.autoDismissMs))) {
            return invalid(ValidationReason.STEP_AUTO_DISMISS_MS_INVALID);
        }
        autoDismissMs = value.autoDismissMs;
    }
    const i18n = parseI18n(value.i18n);
    if (!i18n) {
        return null;
    }
    if (!allReferencedKeysExistInEn(title, body, ctaButton, links, i18n)) {
        return invalid(ValidationReason.STEP_I18N_KEY_MISSING_IN_EN);
    }
    const step = {
        id: value.id,
        minElapsedDays: value.minElapsedDays,
        title,
        body,
        i18n,
    };
    if (maxElapsedDays !== undefined) {
        step.maxElapsedDays = maxElapsedDays;
    }
    if (ctaButton) {
        step.ctaButton = ctaButton;
    }
    if (links) {
        step.links = links;
    }
    if (showCloseButton !== undefined) {
        step.showCloseButton = showCloseButton;
    }
    if (autoDismissMs !== undefined) {
        step.autoDismissMs = autoDismissMs;
    }
    return step;
}
export function getRemoteOpdConfig(raw, allowedUrlHosts) {
    if (!isObject(raw)) {
        return invalid(ValidationReason.CONFIG_MISSING);
    }
    if (!isPositiveNumber(raw.trialDurationDays)) {
        return invalid(ValidationReason.TRIAL_DURATION_DAYS_INVALID);
    }
    let requireOptIn;
    if (raw.requireOptIn !== undefined) {
        if (typeof raw.requireOptIn !== "boolean") {
            return invalid(ValidationReason.REQUIRE_OPT_IN_INVALID);
        }
        requireOptIn = raw.requireOptIn;
    }
    if (!Array.isArray(raw.steps) || raw.steps.length === 0) {
        return invalid(ValidationReason.STEPS_INVALID);
    }
    const steps = [];
    for (const stepValue of raw.steps) {
        const step = parseStep(stepValue, allowedUrlHosts);
        if (!step) {
            return null;
        }
        steps.push(step);
    }
    const config = {
        trialDurationDays: raw.trialDurationDays,
        steps,
    };
    if (requireOptIn !== undefined) {
        config.requireOptIn = requireOptIn;
    }
    return config;
}
